import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import FileUpload from '../components/FileUpload';
import SchemaDetection from '../components/SchemaDetection';
import { parseFile, detectVoterFields, getSampleData } from '../utils/fileParser';
import { uploadVoterData, analyzeSchema, geocodeAddresses } from '../services/api';

const STEPS = [
  { id: 'upload', title: 'Upload File' },
  { id: 'schema', title: 'Configure Schema' },
  { id: 'preview', title: 'Preview & Import' }
];

function StepIndicator({ steps, currentStep }) {
  return (
    <div className="flex items-center justify-center mb-8">
      {steps.map((step, index) => {
        const isCompleted = index < currentStep;
        const isCurrent = index === currentStep;

        return (
          <React.Fragment key={step.id}>
            <div className="flex items-center">
              <div
                className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${isCompleted
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : isCurrent
                      ? 'border-blue-600 text-blue-600'
                      : 'border-gray-300 text-gray-400'
                  }`}
              >
                {isCompleted ? (
                  <CheckCircleIcon className="w-5 h-5" />
                ) : (
                  <span className="text-sm font-medium">{index + 1}</span>
                )}
              </div>
              <span
                className={`ml-2 text-sm font-medium ${isCurrent ? 'text-blue-600' : 'text-gray-500'
                  }`}
              >
                {step.title}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`w-16 h-0.5 mx-4 ${isCompleted ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function DataPreview({ data, columns }) {
  if (!data || data.length === 0) return null;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {columns.slice(0, 8).map(col => (
              <th
                key={col}
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                {col}
              </th>
            ))}
            {columns.length > 8 && (
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">
                +{columns.length - 8} more
              </th>
            )}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {columns.slice(0, 8).map(col => (
                <td
                  key={col}
                  className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap"
                >
                  {row[col] !== undefined ? String(row[col]).slice(0, 30) : ''}
                  {String(row[col] || '').length > 30 && '...'}
                </td>
              ))}
              {columns.length > 8 && (
                <td className="px-4 py-3 text-sm text-gray-400">...</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function DataImport() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);

  // File and data state
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [parseError, setParseError] = useState(null);

  // Schema state
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [fieldMapping, setFieldMapping] = useState({});
  const [visualizationConfig, setVisualizationConfig] = useState(null);

  // Import settings
  const [datasetName, setDatasetName] = useState('');
  const [description, setDescription] = useState('');

  // Default location settings (for geocoding incomplete addresses)
  const [defaultCity, setDefaultCity] = useState('');
  const [defaultState, setDefaultState] = useState('');
  const [defaultCounty, setDefaultCounty] = useState('');

  // Geographic outlier rejection
  const [rejectOutliers, setRejectOutliers] = useState(false);
  const [outlierThreshold, setOutlierThreshold] = useState(50); // Default 50km

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: (formData) => uploadVoterData(formData.file, formData.options),
    onSuccess: (data) => {
      navigate(`/map?dataset=${data.datasetId}`);
    }
  });

  // Handle file selection
  const handleFilesSelected = useCallback(async (files) => {
    if (files.length === 0) return;

    const file = files[0];
    setSelectedFile(file);
    setParseError(null);
    setParsedData(null);
    setDatasetName(file.name.replace(/\.[^/.]+$/, ''));

    try {
      // Check if structured file
      const extension = file.name.split('.').pop().toLowerCase();
      const isStructured = ['csv', 'xlsx', 'xls'].includes(extension);

      if (isStructured) {
        // Parse locally for structured files
        const result = await parseFile(file);
        setParsedData(result);

        // Auto-detect fields
        const { detectedFields, mapping } = detectVoterFields(result.columns);
        setFieldMapping(mapping);
        setAnalysisResult({
          confidence: detectedFields.length / 10, // Rough confidence based on fields detected
          detectedFields,
          dataType: 'Voter Registration Data',
          summary: `Detected ${detectedFields.length} voter-related fields out of ${result.columns.length} columns`
        });
      } else {
        // For unstructured files (PDF, DOCX), we'll use the backend
        setIsAnalyzing(true);
        try {
          const result = await analyzeSchema(file);
          setAnalysisResult(result);
          if (result.extractedData) {
            setParsedData(result.extractedData);
          }
          if (result.suggestedMapping) {
            setFieldMapping(result.suggestedMapping);
          }
        } catch (error) {
          setParseError(`Failed to analyze file: ${error.message}`);
        } finally {
          setIsAnalyzing(false);
        }
      }
    } catch (error) {
      setParseError(`Failed to parse file: ${error.message}`);
      setParsedData(null);
    }
  }, []);

  // Handle mapping change
  const handleMappingChange = useCallback((field, column) => {
    setFieldMapping(prev => ({
      ...prev,
      [field]: column
    }));
  }, []);

  // Handle visualization suggestion accept
  const handleVisualizationAccept = useCallback((suggestion) => {
    setVisualizationConfig({
      colorBy: suggestion.field,
      colorScheme: suggestion.colorScheme,
      labels: suggestion.labels
    });
  }, []);

  // Handle import
  const handleImport = async () => {
    uploadMutation.mutate({
      file: selectedFile,
      options: {
        datasetName,
        description,
        fieldMapping,
        visualizationConfig,
        defaultCity: defaultCity.trim() || null,
        defaultState: defaultState.trim() || null,
        defaultCounty: defaultCounty.trim() || null,
        rejectOutliers,
        outlierThreshold: rejectOutliers ? outlierThreshold : null
      }
    });
  };

  // Re-analyze
  const handleReanalyze = useCallback(async () => {
    if (!selectedFile) return;

    setIsAnalyzing(true);
    try {
      const result = await analyzeSchema(selectedFile);
      setAnalysisResult(result);
      if (result.suggestedMapping) {
        setFieldMapping(result.suggestedMapping);
      }
    } catch (error) {
      setParseError(`Failed to re-analyze: ${error.message}`);
    } finally {
      setIsAnalyzing(false);
    }
  }, [selectedFile]);

  // Navigation
  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return selectedFile && parsedData && !parseError;
      case 1:
        return Object.keys(fieldMapping).length > 0;
      case 2:
        return datasetName.trim() !== '';
      default:
        return false;
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Import Voter Data</h1>
        <p className="mt-1 text-gray-600">
          Upload and configure your voter data for visualization
        </p>
      </div>

      {/* Step Indicator */}
      <StepIndicator steps={STEPS} currentStep={currentStep} />

      {/* Step Content */}
      <div className="bg-white rounded-lg shadow p-6">
        {/* Step 1: Upload */}
        {currentStep === 0 && (
          <div className="space-y-6">
            <FileUpload
              onFilesSelected={handleFilesSelected}
              maxFiles={1}
            />

            {parseError && (
              <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                <span className="text-sm text-red-700">{parseError}</span>
              </div>
            )}

            {parsedData && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircleIcon className="h-5 w-5 text-green-500" />
                  <span className="text-sm text-green-700">
                    Successfully parsed {parsedData.rowCount.toLocaleString()} rows
                    with {parsedData.columns.length} columns
                  </span>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Data Preview</h3>
                  <DataPreview
                    data={getSampleData(parsedData.data, 5)}
                    columns={parsedData.columns}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Schema Configuration */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <SchemaDetection
              isAnalyzing={isAnalyzing}
              analysisResult={analysisResult}
              availableColumns={parsedData?.columns || []}
              mapping={fieldMapping}
              onMappingChange={handleMappingChange}
              onVisualizationAccept={handleVisualizationAccept}
              onVisualizationModify={(suggestion) => {
                setVisualizationConfig(suggestion);
              }}
              onReanalyze={handleReanalyze}
            />
          </div>
        )}

        {/* Step 3: Preview & Import */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Dataset Name
              </label>
              <input
                type="text"
                value={datasetName}
                onChange={(e) => setDatasetName(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter a name for this dataset"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Description (optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="Add a description for this dataset"
              />
            </div>

            {/* Default Location Settings */}
            <div className="border-t pt-6">
              <h3 className="text-sm font-medium text-gray-900 mb-1">Default Location</h3>
              <p className="text-sm text-gray-500 mb-4">
                If your data doesn't include city/state/county, enter defaults here for geocoding.
                This is common for local walk lists where all addresses are in the same area.
              </p>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    City
                  </label>
                  <input
                    type="text"
                    value={defaultCity}
                    onChange={(e) => setDefaultCity(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Las Cruces"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    State
                  </label>
                  <input
                    type="text"
                    value={defaultState}
                    onChange={(e) => setDefaultState(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., NM"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    County
                  </label>
                  <input
                    type="text"
                    value={defaultCounty}
                    onChange={(e) => setDefaultCounty(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Dona Ana"
                  />
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Import Summary</h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500">File:</dt>
                  <dd className="text-gray-900">{selectedFile?.name}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Records:</dt>
                  <dd className="text-gray-900">{parsedData?.rowCount.toLocaleString()}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Mapped Fields:</dt>
                  <dd className="text-gray-900">{Object.keys(fieldMapping).length}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Address Fields:</dt>
                  <dd className="text-gray-900">
                    {['house', 'houseNumber', 'street', 'city', 'state', 'zip'].filter(f => fieldMapping[f]).length} detected
                  </dd>
                </div>
              </dl>
            </div>

            {/* Geocoding notice */}
            {['house', 'houseNumber', 'street', 'city', 'state', 'zip'].some(f => fieldMapping[f]) && (
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="text-blue-600 mt-0.5">
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-blue-900">
                      Geocoding will be performed
                    </div>
                    <div className="text-sm text-blue-700 mt-1">
                      Addresses will be converted to coordinates after import.
                      This may take a few minutes for large datasets.
                    </div>
                  </div>
                </div>

                {/* Geographic outlier rejection option */}
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rejectOutliers}
                      onChange={(e) => setRejectOutliers(e.target.checked)}
                      className="mt-0.5 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        Reject geographic outliers
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        Automatically detect and re-geocode addresses that appear far from the main cluster.
                        Uses a secondary geocoding service (US Census) to find more accurate locations for outliers.
                      </div>
                    </div>
                  </label>

                  {/* Threshold configuration - shown when checkbox is checked */}
                  {rejectOutliers && (
                    <div className="mt-4 ml-7 p-3 bg-white border border-gray-200 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Outlier Distance Threshold
                      </label>
                      <div className="flex items-center gap-4">
                        <input
                          type="range"
                          min="10"
                          max="200"
                          step="10"
                          value={outlierThreshold}
                          onChange={(e) => setOutlierThreshold(Number(e.target.value))}
                          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                        />
                        <div className="flex items-center gap-2 min-w-[100px]">
                          <input
                            type="number"
                            min="10"
                            max="200"
                            value={outlierThreshold}
                            onChange={(e) => setOutlierThreshold(Math.max(10, Math.min(200, Number(e.target.value))))}
                            className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                          />
                          <span className="text-sm text-gray-600">km</span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Points more than {outlierThreshold} km from the center will be considered outliers and re-geocoded.
                        Records that fail re-geocoding will be listed as "ungeocoded" on the map.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
          <button
            onClick={() => setCurrentStep(prev => prev - 1)}
            disabled={currentStep === 0}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back
          </button>

          {currentStep < STEPS.length - 1 ? (
            <button
              onClick={() => setCurrentStep(prev => prev + 1)}
              disabled={!canProceed()}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ArrowRightIcon className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={handleImport}
              disabled={!canProceed() || uploadMutation.isPending}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploadMutation.isPending ? 'Importing...' : 'Import Dataset'}
              <CheckCircleIcon className="h-5 w-5" />
            </button>
          )}
        </div>

        {uploadMutation.isError && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <span className="text-sm text-red-700">
              Import failed: {uploadMutation.error?.message || 'Unknown error'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
